/**
 * Build a concise label for a DOM element.
 * Format: tag[.firstClass][[name]][ "aria-label"]
 *
 * Examples:
 *   input.file[email] "Email address"
 *   nav "Main navigation"
 *   div.flex
 *   button "Submit"
 */
const MAX_ARIA_LENGTH = 30;
const FORM_TAGS = new Set(['INPUT', 'SELECT', 'TEXTAREA']);
/** Tailwind-style prefixes and single-purpose utility patterns to skip */
const UTILITY_CLASS_RE = /^(w-|h-|p-|m-|bg-|text-|flex|grid|hidden|absolute|relative|fixed|border|rounded|shadow|outline|ring|transition|opacity|overflow|cursor|pointer|select-|z-|gap-|space-|items-|justify-|self-|font-|leading-|tracking-|whitespace-|break-|align-|max-|min-|inset-|top-|right-|bottom-|left-|block|inline|table|sr-only|not-sr-only|px-|py-|pt-|pr-|pb-|pl-|mt-|mr-|mb-|ml-|mx-|my-|col-|row-)/;
/** Classes with variant prefixes like file:, hover:, focus:, dark:, md:, aria-invalid: */
const VARIANT_PREFIX_RE = /^[a-z][\w-]*:/;
function findMeaningfulClass(classList) {
    for (let i = 0; i < classList.length; i++) {
        const cls = classList[i];
        if (!VARIANT_PREFIX_RE.test(cls) && !UTILITY_CLASS_RE.test(cls) && cls.length > 1) {
            return cls;
        }
    }
    // If all classes are utilities, just skip — show tag only
    return null;
}
function buildElementLabel(element) {
    const tag = element.tagName.toLowerCase();
    // First meaningful class (skip Tailwind utilities/variants)
    const firstClass = findMeaningfulClass(element.classList);
    let label = firstClass ? `${tag}.${firstClass}` : tag;
    // Form name attribute
    const nameAttr = element.getAttribute('name');
    if (nameAttr && FORM_TAGS.has(element.tagName)) {
        label += `[${nameAttr}]`;
    }
    // Aria-label for context
    const ariaLabel = element.getAttribute('aria-label');
    if (ariaLabel && ariaLabel.trim().length > 0) {
        const trimmed = ariaLabel.trim();
        const text = trimmed.length > MAX_ARIA_LENGTH
            ? trimmed.slice(0, MAX_ARIA_LENGTH) + '\u2026'
            : trimmed;
        label += ` "${text}"`;
    }
    return label;
}

/**
 * Custom logger utility for Feedbacker
 * Provides environment-aware logging that can be disabled in production
 */
var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["DEBUG"] = 0] = "DEBUG";
    LogLevel[LogLevel["INFO"] = 1] = "INFO";
    LogLevel[LogLevel["WARN"] = 2] = "WARN";
    LogLevel[LogLevel["ERROR"] = 3] = "ERROR";
    LogLevel[LogLevel["NONE"] = 4] = "NONE";
})(LogLevel || (LogLevel = {}));
class Logger {
    config;
    isDevelopment;
    isTest;
    constructor() {
        this.isDevelopment = "development" === 'development';
        this.isTest = "development" === 'test';
        // Default configuration
        this.config = {
            level: this.getDefaultLogLevel(),
            prefix: '[Detection]',
            enabled: this.isDevelopment || this.isTest,
            timestamp: false
        };
        // Allow runtime configuration via window object in browser
        if (typeof window !== 'undefined' && window.__DETECTION_LOG_LEVEL__) {
            this.config.level = window.__DETECTION_LOG_LEVEL__;
        }
    }
    getDefaultLogLevel() {
        return LogLevel.DEBUG; // Everything in development
    }
    shouldLog(level) {
        return this.config.enabled && level >= this.config.level;
    }
    formatMessage(level, message, ...args) {
        const timestamp = this.config.timestamp ? `[${new Date().toISOString()}] ` : '';
        const levelStr = `[${level}]`;
        return [`${timestamp}${this.config.prefix} ${levelStr} ${message}`, ...args];
    }
    debug(message, ...args) {
        if (this.shouldLog(LogLevel.DEBUG)) {
            // eslint-disable-next-line no-console
            console.log(...this.formatMessage('DEBUG', message, ...args));
        }
    }
    info(message, ...args) {
        if (this.shouldLog(LogLevel.INFO)) {
            // eslint-disable-next-line no-console
            console.log(...this.formatMessage('INFO', message, ...args));
        }
    }
    log(message, ...args) {
        this.info(message, ...args);
    }
    warn(message, ...args) {
        if (this.shouldLog(LogLevel.WARN)) {
            // eslint-disable-next-line no-console
            console.warn(...this.formatMessage('WARN', message, ...args));
        }
    }
    error(message, ...args) {
        if (this.shouldLog(LogLevel.ERROR)) {
            // eslint-disable-next-line no-console
            console.error(...this.formatMessage('ERROR', message, ...args));
        }
    }
    /**
     * Group related logs together (only in development)
     */
    group(label) {
        if (this.shouldLog(LogLevel.DEBUG)) {
            // eslint-disable-next-line no-console
            console.group(`${this.config.prefix} ${label}`);
        }
    }
    groupEnd() {
        if (this.shouldLog(LogLevel.DEBUG)) {
            // eslint-disable-next-line no-console
            console.groupEnd();
        }
    }
    /**
     * Create a table output (only in development)
     */
    table(data) {
        if (this.shouldLog(LogLevel.DEBUG)) {
            // eslint-disable-next-line no-console
            console.table(data);
        }
    }
    /**
     * Time measurement utilities
     */
    time(label) {
        if (this.shouldLog(LogLevel.DEBUG)) {
            // eslint-disable-next-line no-console
            console.time(`${this.config.prefix} ${label}`);
        }
    }
    timeEnd(label) {
        if (this.shouldLog(LogLevel.DEBUG)) {
            // eslint-disable-next-line no-console
            console.timeEnd(`${this.config.prefix} ${label}`);
        }
    }
    /**
     * Configure the logger at runtime
     */
    configure(config) {
        this.config = { ...this.config, ...config };
    }
    /**
     * Enable or disable logging
     */
    enable() {
        this.config.enabled = true;
    }
    disable() {
        this.config.enabled = false;
    }
    /**
     * Set log level
     */
    setLevel(level) {
        this.config.level = level;
    }
    /**
     * Get current configuration
     */
    getConfig() {
        return { ...this.config };
    }
}
// Create singleton instance
const logger = new Logger();

/**
 * Component Detection Strategy Chain Pattern
 * Base class and chain management for component detection strategies
 */
/**
 * Abstract base class for component detection strategies
 */
class DetectionStrategy {
    next;
    /**
     * Set the next strategy in the chain
     */
    setNext(strategy) {
        this.next = strategy;
        return strategy;
    }
    /**
     * Handle component detection - implements chain of responsibility
     */
    handle(element) {
        const result = this.detect(element);
        if (result) {
            return result;
        }
        if (this.next) {
            return this.next.handle(element);
        }
        return null;
    }
    /**
     * Helper method to build component path from React fiber
     */
    buildComponentPath(fiber) {
        const path = [];
        let current = fiber;
        const maxDepth = 10; // Limit depth to avoid too long paths
        let depth = 0;
        // Common wrapper components to filter out
        const wrappers = new Set([
            'FeedbackProvider',
            'FeedbackErrorBoundary',
            'FeedbackProviderInternal',
            'FeedbackContextProvider',
            'ComponentDetectionProvider',
            'ErrorBoundary',
            'Provider',
            'Consumer',
            'Context',
            'Fragment',
            'Suspense',
            'StrictMode',
            'App' // Keep App but filter if it's just a wrapper
        ]);
        let iterations = 0;
        const maxIterations = 100;
        while (current && depth < maxDepth && iterations < maxIterations) {
            iterations++;
            if (current.type && typeof current.type === 'function') {
                const componentName = current.type.displayName || current.type.name;
                if (componentName && !wrappers.has(componentName)) {
                    // Add to path if it's not a wrapper component
                    path.unshift(componentName);
                    depth++;
                }
                else if (componentName === 'App' && path.length === 0) {
                    // Only add App if there are no other components yet
                    path.unshift(componentName);
                    depth++;
                }
            }
            else if (current.type && typeof current.type === 'string') ;
            current = current.return;
        }
        // If path is empty, at least show something meaningful
        if (path.length === 0) {
            path.push('Component');
        }
        return path;
    }
    /**
     * Helper method to build hybrid path (Components > HTML elements)
     * Shows React components followed by DOM path to the selected element
     */
    buildHybridPath(element, _fiber) {
        // Step 1: Build DOM path from selected element up to nearest React component
        const domPath = [];
        let currentElement = element;
        let parentComponentFiber = null;
        // Find the nearest parent React component
        const maxDomDepth = 30;
        let domIterations = 0;
        while (currentElement && !parentComponentFiber && domIterations < maxDomDepth) {
            domIterations++;
            // Add current element to DOM path
            domPath.unshift(buildElementLabel(currentElement));
            // Check if this element has a React fiber with a component
            const elementFiber = this.getReactFiber(currentElement);
            if (elementFiber) {
                // Walk up the fiber tree to find the nearest React component
                let fiberCurrent = elementFiber;
                let fiberIterations = 0;
                const maxFiberDepth = 30;
                while (fiberCurrent && fiberIterations < maxFiberDepth) {
                    fiberIterations++;
                    if (fiberCurrent.type && typeof fiberCurrent.type === 'function') {
                        const componentName = fiberCurrent.type.displayName || fiberCurrent.type.name;
                        if (componentName && this.isValidReactComponent(componentName)) {
                            parentComponentFiber = fiberCurrent;
                            break;
                        }
                    }
                    fiberCurrent = fiberCurrent.return;
                }
            }
            currentElement = currentElement.parentElement;
        }
        // Step 2: Build React component path from the parent component upwards
        const componentPath = [];
        let current = parentComponentFiber;
        const maxDepth = 10;
        let depth = 0;
        const wrappers = new Set([
            'FeedbackProvider',
            'FeedbackErrorBoundary',
            'FeedbackProviderInternal',
            'FeedbackContextProvider',
            'ComponentDetectionProvider',
            'ErrorBoundary',
            'Provider',
            'Consumer',
            'Context',
            'Fragment',
            'Suspense',
            'StrictMode'
        ]);
        let compIterations = 0;
        const maxCompIterations = 100;
        while (current && depth < maxDepth && compIterations < maxCompIterations) {
            compIterations++;
            if (current.type && typeof current.type === 'function') {
                const componentName = current.type.displayName || current.type.name;
                if (componentName && !wrappers.has(componentName)) {
                    componentPath.unshift(componentName);
                    depth++;
                }
            }
            current = current.return;
        }
        // Step 3: Combine paths (Components > DOM elements)
        return [...componentPath, ...domPath];
    }
    /**
     * Check if a name is a valid React component (not a wrapper)
     */
    isValidReactComponent(name) {
        const wrappers = [
            'FeedbackProvider',
            'FeedbackErrorBoundary',
            'FeedbackProviderInternal',
            'FeedbackContextProvider',
            'ComponentDetectionProvider',
            'ErrorBoundary',
            'Provider',
            'Consumer',
            'Context',
            'Fragment',
            'Suspense',
            'StrictMode'
        ];
        return (!wrappers.includes(name) &&
            !name.includes('Provider') &&
            !name.includes('Context') &&
            name !== 'Anonymous' &&
            name !== 'Component');
    }
    /**
     * Helper method to extract props from React fiber (safely)
     */
    extractProps(fiber) {
        try {
            if (fiber?.memoizedProps) {
                const props = { ...fiber.memoizedProps };
                // Remove children and other internal props for security
                delete props.children;
                delete props.dangerouslySetInnerHTML;
                return props;
            }
        }
        catch (error) {
            logger.warn('Error extracting props:', error);
        }
        return undefined;
    }
    /**
     * Helper method to get React fiber from DOM element
     */
    getReactFiber(element) {
        // React attaches fiber to DOM elements with keys like __reactFiber$ or __reactInternalInstance$
        const fiberKeys = Object.keys(element).filter((key) => key.startsWith('__reactFiber') || key.startsWith('__reactInternalInstance'));
        if (fiberKeys.length > 0) {
            return element[fiberKeys[0]];
        }
        return null;
    }
    /**
     * Helper method to sanitize component name
     */
    sanitizeComponentName(name) {
        // Remove any potentially unsafe characters and limit length
        return name.replace(/[^a-zA-Z0-9_$]/g, '').substring(0, 100);
    }
}
/**
 * Detection Strategy Chain Manager
 * Sets up and manages the chain of detection strategies
 */
class DetectionChain {
    firstStrategy;
    /**
     * Build the complete detection chain
     */
    buildChain(devToolsStrategy, fiberStrategy, heuristicStrategy, fallbackStrategy) {
        // Set up the chain: DevTools -> Fiber -> Heuristic -> Fallback
        devToolsStrategy.setNext(fiberStrategy).setNext(heuristicStrategy).setNext(fallbackStrategy);
        this.firstStrategy = devToolsStrategy;
        return this.firstStrategy;
    }
    /**
     * Detect component using the complete strategy chain
     */
    detectComponent(element) {
        if (!this.firstStrategy) {
            throw new Error('Detection chain not built. Call buildChain() first.');
        }
        try {
            return this.firstStrategy.handle(element);
        }
        catch (error) {
            logger.error('Component detection error:', error);
            return null;
        }
    }
}

/**
 * DevToolsStrategy - Uses React DevTools hook for component detection
 * This is the most reliable method when DevTools are available
 */
class DevToolsStrategy extends DetectionStrategy {
    reactDevTools = null;
    constructor() {
        super();
        this.initializeDevTools();
    }
    /**
     * Initialize React DevTools hook if available
     */
    initializeDevTools() {
        try {
            // Check for React DevTools hook
            if (typeof window !== 'undefined' && window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
                this.reactDevTools = window.__REACT_DEVTOOLS_GLOBAL_HOOK__;
            }
        }
        catch (error) {
            logger.warn('DevTools not available:', error);
        }
    }
    /**
     * Detect component using React DevTools hook
     */
    detect(element) {
        if (!this.reactDevTools) {
            return null;
        }
        try {
            // Try to get the React fiber through DevTools
            const fiber = this.getDevToolsFiber(element);
            if (!fiber) {
                return null;
            }
            const componentName = this.getComponentNameFromFiber(fiber);
            if (!componentName) {
                return null;
            }
            const path = this.buildComponentPath(fiber);
            const props = this.extractProps(fiber);
            return {
                name: this.sanitizeComponentName(componentName),
                path,
                element,
                props,
                fiber
            };
        }
        catch (error) {
            logger.warn('DevTools detection failed:', error);
            return null;
        }
    }
    /**
     * Get React fiber using DevTools hook
     */
    getDevToolsFiber(element) {
        if (!this.reactDevTools) {
            return null;
        }
        try {
            // DevTools provides a findFiberByHostInstance method
            if (this.reactDevTools.findFiberByHostInstance) {
                return this.reactDevTools.findFiberByHostInstance(element);
            }
            // Fallback to checking renderers
            if (this.reactDevTools.renderers && this.reactDevTools.renderers.size > 0) {
                for (const renderer of this.reactDevTools.renderers.values()) {
                    if (renderer.findFiberByHostInstance) {
                        const fiber = renderer.findFiberByHostInstance(element);
                        if (fiber) {
                            return fiber;
                        }
                    }
                }
            }
        }
        catch (error) {
            logger.warn('DevTools fiber lookup failed:', error);
        }
        return null;
    }
    /**
     * Extract component name from React fiber
     */
    getComponentNameFromFiber(fiber) {
        if (!fiber) {
            return null;
        }
        try {
            // Check for function components
            if (fiber.type && typeof fiber.type === 'function') {
                return fiber.type.displayName || fiber.type.name || null;
            }
            // Check for class components
            if (fiber.stateNode && fiber.stateNode.constructor) {
                return fiber.stateNode.constructor.displayName || fiber.stateNode.constructor.name || null;
            }
            // Check for forwardRef components
            if (fiber.type && fiber.type.$$typeof) {
                const typeString = fiber.type.$$typeof.toString();
                if (typeString.includes('react.forward_ref') && fiber.type.render) {
                    return fiber.type.render.displayName || fiber.type.render.name || 'ForwardRef';
                }
                if (typeString.includes('react.memo') && fiber.type.type) {
                    return fiber.type.type.displayName || fiber.type.type.name || 'Memo';
                }
            }
        }
        catch (error) {
            logger.warn('Error extracting component name:', error);
        }
        return null;
    }
}

/**
 * FiberStrategy - Direct React fiber inspection for component detection
 * Used when DevTools are not available but React fiber data is accessible
 */
class FiberStrategy extends DetectionStrategy {
    /**
     * Detect component by directly inspecting React fiber
     */
    detect(element) {
        try {
            const fiber = this.getReactFiber(element);
            if (!fiber) {
                return null;
            }
            // Use hybrid path instead of component-only path
            const path = this.buildHybridPath(element, fiber);
            // Extract the component name from the path or fiber
            const componentName = this.extractComponentNameFromPath(path) ||
                this.getComponentNameFromFiber(fiber) ||
                'Component';
            const props = this.extractProps(fiber);
            return {
                name: this.sanitizeComponentName(componentName),
                path,
                element,
                props,
                fiber
            };
        }
        catch (error) {
            logger.warn('Fiber detection failed:', error);
            return null;
        }
    }
    /**
     * Extract component name from hybrid path
     */
    extractComponentNameFromPath(path) {
        // Find the last React component in the path (before HTML elements)
        for (let i = path.length - 1; i >= 0; i--) {
            const segment = path[i];
            if (!segment) {
                continue;
            }
            // Check if it's an HTML element (lowercase or contains .)
            if (!/^[a-z]/.test(segment) && !segment.includes('.')) {
                return segment;
            }
        }
        return null;
    }
    /**
     * Extract component name from React fiber
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getComponentNameFromFiber(fiber) {
        if (!fiber) {
            return null;
        }
        try {
            // First try to get the name from the immediate fiber
            const immediateName = this.extractNameFromFiber(fiber);
            if (immediateName && this.isValidComponentName(immediateName)) {
                return immediateName;
            }
            // Walk up the fiber tree to find a named component
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let current = fiber.return; // Start from parent
            let attempts = 0;
            const maxAttempts = 20; // Prevent infinite loops
            while (current && attempts < maxAttempts) {
                const name = this.extractNameFromFiber(current);
                if (name && this.isValidComponentName(name)) {
                    return name;
                }
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                current = current.return;
                attempts++;
            }
        }
        catch (error) {
            logger.warn('Error walking fiber tree:', error);
        }
        return null;
    }
    /**
     * Check if component name is valid and not a wrapper
     */
    isValidComponentName(name) {
        // Filter out common wrapper/provider components
        const wrappers = [
            'FeedbackProvider',
            'FeedbackErrorBoundary',
            'FeedbackProviderInternal',
            'FeedbackContextProvider',
            'ComponentDetectionProvider',
            'ErrorBoundary',
            'Provider',
            'Consumer',
            'Context',
            'Fragment',
            'Suspense',
            'StrictMode'
        ];
        return (!wrappers.includes(name) &&
            !name.includes('Provider') &&
            !name.includes('Context') &&
            name !== 'Anonymous' &&
            name !== 'Component');
    }
    /**
     * Extract name from a single fiber node
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    extractNameFromFiber(fiber) {
        if (!fiber) {
            return null;
        }
        try {
            // Function components
            if (fiber.type && typeof fiber.type === 'function') {
                const name = fiber.type.displayName || fiber.type.name;
                if (name && name !== 'Anonymous') {
                    return name;
                }
            }
            // Class components
            if (fiber.stateNode && fiber.stateNode.constructor) {
                const name = fiber.stateNode.constructor.displayName || fiber.stateNode.constructor.name;
                if (name && name !== 'Object') {
                    return name;
                }
            }
            // Handle special React types
            if (fiber.type && fiber.type.$$typeof) {
                return this.handleSpecialReactTypes(fiber);
            }
            // Memoized components
            if (fiber.elementType && typeof fiber.elementType === 'function') {
                const name = fiber.elementType.displayName || fiber.elementType.name;
                if (name && name !== 'Anonymous') {
                    return name;
                }
            }
        }
        catch (error) {
            logger.warn('Error extracting fiber name:', error);
        }
        return null;
    }
    /**
     * Handle special React component types (forwardRef, memo, etc.)
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleSpecialReactTypes(fiber) {
        if (!fiber.type?.$$typeof) {
            return null;
        }
        try {
            const symbolString = fiber.type.$$typeof.toString();
            // React.forwardRef
            if (symbolString.includes('react.forward_ref')) {
                if (fiber.type.render) {
                    const name = fiber.type.render.displayName || fiber.type.render.name;
                    return name ? `ForwardRef(${name})` : 'ForwardRef';
                }
                return 'ForwardRef';
            }
            // React.memo
            if (symbolString.includes('react.memo')) {
                if (fiber.type.type) {
                    const name = fiber.type.type.displayName || fiber.type.type.name;
                    return name ? `Memo(${name})` : 'Memo';
                }
                return 'Memo';
            }
            // React.lazy
            if (symbolString.includes('react.lazy')) {
                return 'Lazy';
            }
            // React.Suspense
            if (symbolString.includes('react.suspense')) {
                return 'Suspense';
            }
            // React.Fragment
            if (symbolString.includes('react.fragment')) {
                return 'Fragment';
            }
            // React.Context.Provider
            if (symbolString.includes('react.provider')) {
                return 'ContextProvider';
            }
            // React.Context.Consumer
            if (symbolString.includes('react.consumer')) {
                return 'ContextConsumer';
            }
        }
        catch (error) {
            logger.warn('Error handling special React types:', error);
        }
        return null;
    }
}

/**
 * HeuristicStrategy - DOM-based heuristic analysis for component detection
 * Used when React fiber information is not available
 */
class HeuristicStrategy extends DetectionStrategy {
    /**
     * Detect component using DOM heuristics
     */
    detect(element) {
        try {
            // Build a hybrid-style path even without fiber data
            const path = this.buildHeuristicHybridPath(element);
            // Extract component name from the path or guess it
            const componentName = this.extractComponentFromPath(path) || this.guessComponentName(element) || 'Component';
            return {
                name: this.sanitizeComponentName(componentName),
                path,
                element,
                props: undefined, // No props available in heuristic mode
                fiber: undefined
            };
        }
        catch (error) {
            logger.warn('Heuristic detection failed:', error);
            return null;
        }
    }
    /**
     * Build a hybrid-style path using heuristics (no fiber data)
     */
    buildHeuristicHybridPath(element) {
        const domPath = [];
        const componentPath = [];
        let currentElement = element;
        let foundComponent = false;
        let depth = 0;
        const maxDepth = 10;
        // Step 1: Build path from element upwards, trying to identify components
        while (currentElement && depth < maxDepth) {
            currentElement.tagName.toLowerCase();
            currentElement.className;
            // Try to identify if this might be a React component
            const possibleComponentName = this.guessComponentName(currentElement);
            if (possibleComponentName && !foundComponent) {
                // Found a likely component - switch to component path
                foundComponent = true;
                componentPath.unshift(possibleComponentName);
            }
            else if (!foundComponent) {
                // Still in DOM elements — use concise label
                domPath.unshift(buildElementLabel(currentElement));
            }
            else {
                // We're above the first component, look for parent components
                const parentComponentName = this.guessComponentName(currentElement);
                if (parentComponentName) {
                    componentPath.unshift(parentComponentName);
                }
            }
            currentElement = currentElement.parentElement;
            depth++;
        }
        // If we didn't find any components, treat the whole thing as DOM path
        if (componentPath.length === 0 && domPath.length > 0) {
            // Try to infer a component from the context
            const inferredComponent = 'Component';
            componentPath.push(inferredComponent);
        }
        // Combine paths
        return [...componentPath, ...domPath];
    }
    /**
     * Extract component name from path
     */
    extractComponentFromPath(path) {
        for (let i = path.length - 1; i >= 0; i--) {
            const segment = path[i];
            if (!segment) {
                continue;
            }
            // Check if it's not an HTML element
            if (!/^[a-z]/.test(segment) && !segment.includes('.')) {
                return segment;
            }
        }
        return null;
    }
    /**
     * Guess component name from DOM element using various heuristics
     */
    guessComponentName(element) {
        // Strategy 1: Look for data attributes that might indicate component names
        const componentName = this.checkDataAttributes(element);
        if (componentName) {
            return componentName;
        }
        // Strategy 2: Look for CSS class names that might be component names
        const className = this.guessFromClassName(element);
        if (className) {
            return className;
        }
        // Strategy 3: Look for semantic HTML elements that might represent components
        const semanticName = this.guessFromSemanticHTML(element);
        if (semanticName) {
            return semanticName;
        }
        // Strategy 4: Look at parent elements for context
        const contextualName = this.guessFromContext(element);
        if (contextualName) {
            return contextualName;
        }
        return null;
    }
    /**
     * Check data attributes for component hints
     */
    checkDataAttributes(element) {
        // Common data attributes used by React developers
        const possibleAttributes = [
            'data-component',
            'data-component-name',
            'data-react-component',
            'data-testid',
            'data-cy', // Cypress testing
            'data-test' // General testing
        ];
        for (const attr of possibleAttributes) {
            const value = element.getAttribute(attr);
            if (value && this.isValidComponentName(value)) {
                return this.formatComponentName(value);
            }
        }
        return null;
    }
    /**
     * Guess component name from CSS class names
     */
    guessFromClassName(element) {
        const classList = Array.from(element.classList);
        for (const className of classList) {
            // Look for component-like class names (PascalCase or component-style)
            if (this.looksLikeComponentClass(className)) {
                return this.formatComponentName(className);
            }
        }
        return null;
    }
    /**
     * Check if a class name looks like a React component class
     */
    looksLikeComponentClass(className) {
        // Skip common utility classes
        const skipPatterns = [
            /^(bg-|text-|p-|m-|w-|h-|flex|grid|hidden|visible)/i, // Tailwind-like
            /^(btn|card|nav|header|footer|main|content)/i, // Common UI classes
            /^(active|disabled|selected|hover|focus)/i, // State classes
            /^[a-z]/ // Skip lowercase-first classes (likely utility classes)
        ];
        for (const pattern of skipPatterns) {
            if (pattern.test(className)) {
                return false;
            }
        }
        // Look for PascalCase or component-style names
        return (/^[A-Z][a-zA-Z0-9]*$/.test(className) || // PascalCase
            /^[A-Z][a-zA-Z0-9]*-[A-Z][a-zA-Z0-9]*$/.test(className) || // Component-SubComponent
            className.includes('Component') ||
            className.includes('Widget') ||
            className.includes('Module'));
    }
    /**
     * Guess component name from semantic HTML elements
     */
    guessFromSemanticHTML(element) {
        const tagName = element.tagName.toLowerCase();
        // Map semantic HTML to likely component names
        const semanticMap = {
            header: 'Header',
            nav: 'Navigation',
            main: 'MainContent',
            aside: 'Sidebar',
            footer: 'Footer',
            article: 'Article',
            section: 'Section',
            form: 'Form',
            table: 'Table',
            dialog: 'Modal',
            details: 'Accordion'
        };
        if (semanticMap[tagName]) {
            return semanticMap[tagName];
        }
        // Check for role attributes
        const role = element.getAttribute('role');
        if (role) {
            const roleMap = {
                button: 'Button',
                dialog: 'Modal',
                tabpanel: 'TabPanel',
                tab: 'Tab',
                tablist: 'TabList',
                menu: 'Menu',
                menuitem: 'MenuItem',
                tooltip: 'Tooltip',
                alert: 'Alert'
            };
            if (roleMap[role]) {
                return roleMap[role];
            }
        }
        return null;
    }
    /**
     * Guess component name from context (parent elements)
     */
    guessFromContext(element) {
        let parent = element.parentElement;
        let depth = 0;
        const maxDepth = 3;
        while (parent && depth < maxDepth) {
            // Check parent's data attributes or class names
            const parentName = this.checkDataAttributes(parent) || this.guessFromClassName(parent);
            if (parentName) {
                // Create a contextual name
                const tagName = element.tagName.toLowerCase();
                return `${parentName}${this.capitalizeFirst(tagName)}`;
            }
            parent = parent.parentElement;
            depth++;
        }
        return null;
    }
    /**
     * Format component name consistently
     */
    formatComponentName(name) {
        return name
            .replace(/[-_]/g, ' ')
            .split(' ')
            .map((word) => this.capitalizeFirst(word))
            .join('');
    }
    /**
     * Capitalize first letter
     */
    capitalizeFirst(str) {
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }
    /**
     * Check if a string could be a valid component name
     */
    isValidComponentName(name) {
        return (name.length > 0 &&
            name.length < 50 &&
            /^[a-zA-Z][a-zA-Z0-9_-]*$/.test(name) &&
            !['div', 'span', 'p', 'a', 'img', 'button'].includes(name.toLowerCase()));
    }
}

/**
 * FallbackStrategy - Final fallback for component detection
 * Always returns "Unknown Component" with basic element information
 */
class FallbackStrategy extends DetectionStrategy {
    /**
     * Always detect with fallback information - never returns null
     */
    detect(element) {
        try {
            // Create a hybrid-style fallback path
            const path = this.createHybridFallbackPath(element);
            // Create a descriptive fallback name based on the element
            const fallbackName = this.createFallbackName(element);
            // Extract basic element information as "props"
            const elementInfo = this.extractElementInfo(element);
            return {
                name: fallbackName,
                path,
                element,
                props: elementInfo,
                fiber: undefined
            };
        }
        catch (error) {
            logger.warn('Fallback detection failed:', error);
            // Even if there's an error, return basic fallback
            const tagName = element.tagName.toLowerCase();
            const fallbackPath = ['Unknown', tagName];
            return {
                name: 'Unknown Component',
                path: fallbackPath,
                element,
                props: { tagName },
                fiber: undefined
            };
        }
    }
    /**
     * Create a descriptive fallback name based on element characteristics
     */
    createFallbackName(element) {
        const tagName = element.tagName.toLowerCase();
        const id = element.id;
        const classList = Array.from(element.classList);
        // Try to create a more specific name based on available information
        if (id) {
            return `Unknown Component (${tagName}#${id})`;
        }
        // Look for meaningful class names
        const meaningfulClass = classList.find((cls) => cls.length > 2 &&
            !cls.match(/^(w-|h-|p-|m-|bg-|text-|flex|grid|hidden|absolute|relative)/) &&
            cls.match(/^[a-zA-Z][a-zA-Z0-9_-]*$/));
        if (meaningfulClass) {
            return `Unknown Component (${tagName}.${meaningfulClass})`;
        }
        // Check for semantic meaning from content or attributes
        const semanticName = this.getSemanticName(element);
        if (semanticName) {
            return `Unknown Component (${semanticName})`;
        }
        // Default fallback
        return 'Unknown Component';
    }
    /**
     * Create a hybrid-style fallback path with inferred component and DOM hierarchy
     */
    createHybridFallbackPath(element) {
        const domPath = [];
        const componentPath = ['Unknown']; // Default component name
        let current = element;
        let depth = 0;
        const maxDepth = 5;
        // Build DOM path from selected element upwards
        while (current && current !== document.body && depth < maxDepth) {
            const tagName = current.tagName.toLowerCase();
            const id = current.id;
            Array.from(current.classList);
            if (id) {
                domPath.unshift(`${tagName}#${id}`);
            }
            else {
                domPath.unshift(buildElementLabel(current));
            }
            // Try to detect if we've reached a component boundary
            // Look for semantic indicators that might suggest a component
            if (this.looksLikeComponent(current) && componentPath.length === 1) {
                // Replace 'Unknown' with a better guess
                const componentName = this.inferComponentName(current);
                if (componentName) {
                    componentPath[0] = componentName;
                }
            }
            current = current.parentElement;
            depth++;
        }
        // Combine paths (component > DOM elements)
        return [...componentPath, ...domPath];
    }
    /**
     * Check if element looks like it might be a component root
     */
    looksLikeComponent(element) {
        // Check for data attributes that might indicate a component
        if (element.hasAttribute('data-component') ||
            element.hasAttribute('data-testid') ||
            element.hasAttribute('data-react-component')) {
            return true;
        }
        // Check for semantic HTML that might be component roots
        const semanticTags = ['article', 'section', 'aside', 'header', 'footer', 'main', 'nav'];
        if (semanticTags.includes(element.tagName.toLowerCase())) {
            return true;
        }
        // Check for class patterns that suggest components
        const classList = Array.from(element.classList);
        return classList.some((cls) => /^[A-Z]/.test(cls) || // PascalCase
            cls.includes('component') ||
            cls.includes('widget') ||
            cls.includes('module'));
    }
    /**
     * Infer a component name from element characteristics
     */
    inferComponentName(element) {
        // Check data attributes
        const dataComponent = element.getAttribute('data-component');
        if (dataComponent) {
            return dataComponent;
        }
        const dataTestId = element.getAttribute('data-testid');
        if (dataTestId) {
            return this.formatComponentName(dataTestId);
        }
        // Check for PascalCase classes
        const classList = Array.from(element.classList);
        const pascalClass = classList.find((cls) => /^[A-Z][a-zA-Z0-9]*$/.test(cls));
        if (pascalClass) {
            return pascalClass;
        }
        // Use semantic HTML element names
        const tagName = element.tagName.toLowerCase();
        const semanticNames = {
            header: 'Header',
            nav: 'Navigation',
            main: 'Main',
            aside: 'Sidebar',
            footer: 'Footer',
            article: 'Article',
            section: 'Section'
        };
        if (semanticNames[tagName]) {
            return semanticNames[tagName];
        }
        return null;
    }
    /**
     * Format a string as a component name
     */
    formatComponentName(str) {
        return str
            .split(/[-_\s]+/)
            .map((word) => this.capitalizeFirst(word))
            .join('');
    }
    /**
     * Extract basic element information
     */
    extractElementInfo(element) {
        const info = {
            tagName: element.tagName.toLowerCase()
        };
        // Add ID if present
        if (element.id) {
            info.id = element.id;
        }
        // Add classes if present (limit to first few to avoid bloat)
        const classList = Array.from(element.classList).slice(0, 5);
        if (classList.length > 0) {
            info.className = classList.join(' ');
        }
        // Add role if present
        const role = element.getAttribute('role');
        if (role) {
            info.role = role;
        }
        // Add data attributes that might be relevant
        const dataAttributes = {};
        for (const attr of element.attributes) {
            if (attr.name.startsWith('data-') && attr.value) {
                const key = attr.name.replace('data-', '');
                if (key.length < 50 && attr.value.length < 100) {
                    dataAttributes[key] = attr.value;
                }
            }
        }
        if (Object.keys(dataAttributes).length > 0) {
            info.dataAttributes = dataAttributes;
        }
        // Add text content if it's short and meaningful
        const textContent = element.textContent?.trim();
        if (textContent && textContent.length > 0 && textContent.length < 100) {
            info.textContent = textContent.substring(0, 50);
        }
        return info;
    }
    /**
     * Get semantic name from element characteristics
     */
    getSemanticName(element) {
        // Check for ARIA labels
        const ariaLabel = element.getAttribute('aria-label');
        if (ariaLabel && ariaLabel.length < 30) {
            return `labeled "${ariaLabel}"`;
        }
        // Check for button text
        if (element.tagName.toLowerCase() === 'button') {
            const buttonText = element.textContent?.trim();
            if (buttonText && buttonText.length < 20) {
                return `button "${buttonText}"`;
            }
        }
        // Check for input placeholders
        if (element.tagName.toLowerCase() === 'input') {
            const placeholder = element.getAttribute('placeholder');
            if (placeholder && placeholder.length < 30) {
                return `input "${placeholder}"`;
            }
        }
        // Check for link text
        if (element.tagName.toLowerCase() === 'a') {
            const linkText = element.textContent?.trim();
            if (linkText && linkText.length < 20) {
                return `link "${linkText}"`;
            }
        }
        return null;
    }
    /**
     * Capitalize first letter of a string
     */
    capitalizeFirst(str) {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }
}

/**
 * Performance utilities for efficient component scanning and operations
 * Implements requestIdleCallback with fallbacks and debouncing utilities
 */
/**
 * Request idle callback with fallback for unsupported browsers
 */
const requestIdleCallback = (callback, options) => {
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        return window.requestIdleCallback(callback, options);
    }
    // Fallback implementation using setTimeout
    const timeout = options?.timeout || 0;
    const startTime = Date.now();
    return setTimeout(() => {
        const deadline = {
            didTimeout: timeout > 0 && Date.now() - startTime >= timeout,
            timeRemaining: () => Math.max(0, 50 - (Date.now() - startTime))
        };
        callback(deadline);
    }, 1);
};
/**
 * Cancel idle callback with fallback
 */
const cancelIdleCallback = (id) => {
    if (typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(id);
    }
    else {
        clearTimeout(id);
    }
};
/**
 * Debounce function with leading and trailing options
 */
const debounce = (func, wait, options = {}) => {
    let timeoutId;
    let maxTimeoutId;
    let lastCallTime;
    let lastInvokeTime = 0;
    let lastArgs;
    let lastThis;
    let result;
    const { leading = false, trailing = true, maxWait } = options;
    const invokeFunc = (time) => {
        const args = lastArgs;
        const thisArg = lastThis;
        lastArgs = undefined;
        lastThis = undefined;
        lastInvokeTime = time;
        result = func.apply(thisArg, args);
        return result;
    };
    const leadingEdge = (time) => {
        lastInvokeTime = time;
        timeoutId = setTimeout(timerExpired, wait);
        return leading ? invokeFunc(time) : result;
    };
    const remainingWait = (time) => {
        const timeSinceLastCall = time - (lastCallTime || 0);
        const timeSinceLastInvoke = time - lastInvokeTime;
        const timeWaiting = wait - timeSinceLastCall;
        return maxWait !== undefined
            ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke)
            : timeWaiting;
    };
    const shouldInvoke = (time) => {
        if (lastCallTime === undefined) {
            return true;
        }
        const timeSinceLastCall = time - lastCallTime;
        const timeSinceLastInvoke = time - lastInvokeTime;
        return (timeSinceLastCall >= wait ||
            timeSinceLastCall < 0 ||
            (maxWait !== undefined && timeSinceLastInvoke >= maxWait));
    };
    const timerExpired = () => {
        const time = Date.now();
        if (shouldInvoke(time)) {
            return trailingEdge(time);
        }
        timeoutId = setTimeout(timerExpired, remainingWait(time));
    };
    const trailingEdge = (time) => {
        timeoutId = undefined;
        if (trailing && lastArgs) {
            return invokeFunc(time);
        }
        lastArgs = undefined;
        lastThis = undefined;
        return result;
    };
    const cancel = () => {
        if (timeoutId !== undefined) {
            clearTimeout(timeoutId);
        }
        if (maxTimeoutId !== undefined) {
            clearTimeout(maxTimeoutId);
        }
        lastInvokeTime = 0;
        lastArgs = lastCallTime = lastThis = timeoutId = maxTimeoutId = undefined;
    };
    const flush = () => {
        return timeoutId === undefined ? result : trailingEdge(Date.now());
    };
    const debounced = function (...args) {
        const time = Date.now();
        const isInvoking = shouldInvoke(time);
        lastArgs = args;
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        lastThis = this;
        lastCallTime = time;
        if (isInvoking) {
            if (timeoutId === undefined) {
                return leadingEdge(lastCallTime);
            }
            if (maxWait !== undefined) {
                timeoutId = setTimeout(timerExpired, wait);
                return invokeFunc(lastCallTime);
            }
        }
        if (timeoutId === undefined) {
            timeoutId = setTimeout(timerExpired, wait);
        }
        return result;
    };
    debounced.cancel = cancel;
    debounced.flush = flush;
    return debounced;
};
/**
 * Throttle function for limiting execution frequency
 */
const throttle = (func, wait, options = {}) => {
    return debounce(func, wait, {
        leading: true,
        maxWait: wait,
        trailing: true,
        ...options
    });
};
/**
 * Performance monitor for tracking expensive operations
 */
class PerformanceMonitor {
    measurements = new Map();
    mark(name) {
        const startTime = performance.now();
        return () => {
            const endTime = performance.now();
            const duration = endTime - startTime;
            if (!this.measurements.has(name)) {
                this.measurements.set(name, []);
            }
            const times = this.measurements.get(name);
            times.push(duration);
            // Keep only the last 100 measurements
            if (times.length > 100) {
                times.shift();
            }
        };
    }
    getStats(name) {
        const times = this.measurements.get(name);
        if (!times || times.length === 0) {
            return null;
        }
        const min = Math.min(...times);
        const max = Math.max(...times);
        const avg = times.reduce((sum, time) => sum + time, 0) / times.length;
        const count = times.length;
        return { avg, min, max, count };
    }
    clear(name) {
        if (name) {
            this.measurements.delete(name);
        }
        else {
            this.measurements.clear();
        }
    }
}
const performanceMonitor = new PerformanceMonitor();

/**
 * Human-readable name resolution for DOM elements.
 *
 * Resolves a display-friendly name using a 6-step priority chain:
 * 1. aria-label
 * 2. aria-labelledby (resolved via getElementById)
 * 3. Direct text content (skipped for containers with >2 children)
 * 4. Role attribute (capitalized)
 * 5. Component name (if provided and not "Unknown")
 * 6. Tag name fallback (with first class if present)
 */
const MAX_TEXT_LENGTH = 40;
const MAX_CHILDREN_FOR_TEXT = 2;
/**
 * Truncate text to `MAX_TEXT_LENGTH` characters, appending ellipsis if needed.
 */
function truncate(text) {
    const trimmed = text.trim();
    if (trimmed.length <= MAX_TEXT_LENGTH) {
        return trimmed;
    }
    return trimmed.slice(0, MAX_TEXT_LENGTH) + '\u2026';
}
/**
 * Capitalize the first letter of a string.
 */
function capitalize(text) {
    if (text.length === 0)
        return text;
    return text.charAt(0).toUpperCase() + text.slice(1);
}
/**
 * Extract direct text content from an element's child text nodes.
 * Returns null if the element has too many children (container heuristic).
 */
function getDirectTextContent(element) {
    if (element.children.length > MAX_CHILDREN_FOR_TEXT) {
        return null;
    }
    let text = '';
    for (let i = 0; i < element.childNodes.length; i++) {
        const node = element.childNodes[i];
        if (node.nodeType === Node.TEXT_NODE) {
            text += node.textContent ?? '';
        }
    }
    const trimmed = text.trim();
    return trimmed.length > 0 ? trimmed : null;
}
/**
 * Resolve a human-readable name for a DOM element.
 *
 * @param element - The target HTML element
 * @param componentName - Optional React/framework component name
 * @returns A non-empty display name string
 */
function getHumanReadableName(element, componentName) {
    // Step 1: aria-label
    const ariaLabel = element.getAttribute('aria-label');
    if (ariaLabel && ariaLabel.trim().length > 0) {
        return truncate(ariaLabel);
    }
    // Step 2: aria-labelledby
    const labelledBy = element.getAttribute('aria-labelledby');
    if (labelledBy) {
        const labelEl = document.getElementById(labelledBy);
        if (labelEl) {
            const labelText = (labelEl.textContent ?? '').trim();
            if (labelText.length > 0) {
                return truncate(labelText);
            }
        }
    }
    // Step 3: Direct text content (skip containers with >2 children)
    const directText = getDirectTextContent(element);
    if (directText) {
        return truncate(directText);
    }
    // Step 4: Role attribute
    const role = element.getAttribute('role');
    if (role && role.trim().length > 0) {
        return capitalize(role.trim());
    }
    // Step 5: Component name
    if (componentName && componentName !== 'Unknown') {
        return componentName;
    }
    // Step 6: Tag name fallback with concise label
    return buildElementLabel(element);
}

/**
 * @feedbacker/detection
 * Framework-agnostic component detection for React apps
 */
/**
 * Convenience factory to create a fully configured detection chain
 */
function createDetector() {
    const chain = new DetectionChain();
    chain.buildChain(new DevToolsStrategy(), new FiberStrategy(), new HeuristicStrategy(), new FallbackStrategy());
    return chain;
}

export { DetectionChain, DetectionStrategy, DevToolsStrategy, FallbackStrategy, FiberStrategy, HeuristicStrategy, LogLevel, Logger, PerformanceMonitor, buildElementLabel, cancelIdleCallback, createDetector, debounce, getHumanReadableName, logger, performanceMonitor, requestIdleCallback, throttle };
//# sourceMappingURL=index.esm.js.map
